import React from 'react'
import { Tooltip } from '@mui/material'
import { ISafetyTeamRecord, IMinimalPerson, ISafetyTeamDocument } from '../../shared/types'
import { ReactComponent as EditSvg } from '../../../../assets/svg/edit.svg'
import { ReactComponent as DeleteIcon } from '../../../../assets/svg/delete.svg'
import { ReactComponent as CheckSvg } from '../../../../assets/svg/check_24px.svg'
import DriverSelector from '../DriverSelector'
import DriverTypeSelector from '../DriverTypeSelector'
import CustomDatePicker from '../../../LoanerAssignments/components/CustomDatePicker'
import { DocumentUpload } from '../DocumentUpload'
import styles from '../../SafetyTeam.module.scss'

interface IActiveSafetyTeamRowProps {
  record: ISafetyTeamRecord
  onSave: (recordId: string) => void
  onEdit: (recordId: string) => void
  onDelete: (recordId: string) => void
  onFieldChange: (
    recordId: string,
    field: keyof ISafetyTeamRecord,
    value: string
  ) => void
  onDriverSelect: (recordId: string, person: IMinimalPerson) => void
  onDocumentChange: (recordId: string, documents: ISafetyTeamDocument[]) => void
  availablePeople: IMinimalPerson[]
}

const ActiveSafetyTeamRow = ({
  record,
  onSave,
  onEdit,
  onDelete,
  onFieldChange,
  onDriverSelect,
  onDocumentChange,
  availablePeople,
}: IActiveSafetyTeamRowProps) => {
  return (
    <tr key={record.id}>
      <td>
        {record.isEditing ? (
          <input
            type="text"
            className={styles.textInput}
            value={typeof record.driver === 'string' ? record.driver : record.driver?.name || ''}
            onChange={(e) => onFieldChange(record.id, 'driver', e.target.value)}
            placeholder="Enter driver name..."
          />
        ) : (
          <span className={styles.readonlyText}>
            {typeof record.driver === 'string' ? record.driver : record.driver?.name || ''}
          </span>
        )}
      </td>

      <td>
        {record.isEditing ? (
          <input
            type="text"
            className={styles.textInput}
            value={record.driversLicenseNumber}
            onChange={(e) => onFieldChange(record.id, 'driversLicenseNumber', e.target.value)}
            placeholder="Enter license number..."
          />
        ) : (
          <Tooltip title={record.driversLicenseNumber || ''} placement="top">
            <span className={styles.readonlyText}>
              {record.driversLicenseNumber || ' '}
            </span>
          </Tooltip>
        )}
      </td>

      <td>
        {record.isEditing ? (
          <DriverTypeSelector
            selectedType={record.driverType}
            onSelect={(type) => onFieldChange(record.id, 'driverType', type)}
          />
        ) : (
          <span className={styles.readonlyText}>
            {record.driverType || ' '}
          </span>
        )}
      </td>

      <td>
        {record.isEditing ? (
          <CustomDatePicker
            value={record.testDate}
            onChange={(date) => onFieldChange(record.id, 'testDate', date)}
          />
        ) : (
          <span className={styles.readonlyText}>
            {record.testDate || ' '}
          </span>
        )}
      </td>

      <td>
        {record.isEditing ? (
          <CustomDatePicker
            value={record.assignedDate}
            onChange={(date) => onFieldChange(record.id, 'assignedDate', date)}
          />
        ) : (
          <span className={styles.readonlyText}>
            {record.assignedDate || ' '}
          </span>
        )}
      </td>

      <td>
        <DocumentUpload
          documents={record.documents || []}
          recordId={record.id}
          isEditing={record.isEditing}
          onChange={(documents) => onDocumentChange(record.id, documents)}
        />
      </td>

      <td>
        {record.isEditing ? (
          <CustomDatePicker
            value={record.archivedDate}
            onChange={(date) => onFieldChange(record.id, 'archivedDate', date)}
            hint='After clicking "Select", this record will be moved to "Closed".'
          />
        ) : (
          <span className={styles.readonlyText}>
            {record.archivedDate || ' '}
          </span>
        )}
      </td>

      <td>
        <div className={styles.actionButtons}>
          {record.isEditing ? (
            <Tooltip
              title="Save"
              placement="top"
              key={`edit-${record.id}-${record.isEditing}`}
            >
              <button
                className={styles.saveButton}
                onClick={() => onSave(record.id)}
              >
                <CheckSvg />
              </button>
            </Tooltip>
          ) : (
            <Tooltip
              title="Edit"
              placement="top"
              key={`edit-${record.id}-${record.isEditing}`}
            >
              <button
                className={styles.editButton}
                onClick={() => onEdit(record.id)}
              >
                <EditSvg />
              </button>
            </Tooltip>
          )}

          <Tooltip title="Delete" placement="top">
            <button
              className={styles.deleteButton}
              onClick={() => onDelete(record.id)}
            >
              <DeleteIcon />
            </button>
          </Tooltip>
        </div>
      </td>
    </tr>
  )
}

export default ActiveSafetyTeamRow
